import { SITE } from '../config/site';

export type SeoFaqItem = { q: string; a: string };

export type LandingSeoInput = {
  locale: 'tr' | 'en';
  title: string;
  description: string;
  keywords: string;
  faqs: SeoFaqItem[];
};

/** Organization + SoftwareApplication + WebSite + FAQPage for search & AI citation. */
export function buildLandingJsonLd(input: LandingSeoInput): Record<string, unknown>[] {
  const { locale, title, description, faqs } = input;
  const inLanguage = locale === 'tr' ? 'tr-TR' : 'en-US';

  const organization = {
    '@type': 'Organization',
    '@id': `${SITE.url}/#organization`,
    name: SITE.name,
    url: SITE.url,
    email: SITE.email,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE.url}/logo.png`,
    },
    sameAs: SITE.sameAs,
    contactPoint: {
      '@type': 'ContactPoint',
      email: SITE.email,
      contactType: 'customer support',
      availableLanguage: ['Turkish', 'English'],
    },
  };

  const website = {
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    url: SITE.url,
    name: SITE.name,
    description,
    inLanguage: ['tr-TR', 'en-US'],
    publisher: { '@id': `${SITE.url}/#organization` },
  };

  const software = {
    '@type': 'SoftwareApplication',
    '@id': `${SITE.url}/#app`,
    name: SITE.name,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Social Media Management',
    operatingSystem: 'Web',
    url: SITE.url,
    description,
    inLanguage,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '15',
      highPrice: '149',
      offerCount: '4',
    },
    featureList: [
      'AI content generation matched to brand voice',
      'Human approval before publishing',
      'Multi-account social media management',
      'Smart scheduling at peak engagement times',
      'Performance analytics dashboard',
      'Official platform API connections (Instagram first)',
    ],
    creator: { '@id': `${SITE.url}/#organization` },
  };

  const webPage = {
    '@type': 'WebPage',
    '@id': `${SITE.url}/#webpage`,
    url: SITE.url,
    name: title,
    description,
    inLanguage,
    isPartOf: { '@id': `${SITE.url}/#website` },
    about: { '@id': `${SITE.url}/#app` },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: SITE.ogImage,
    },
  };

  const faqPage = {
    '@type': 'FAQPage',
    '@id': `${SITE.url}/#faq`,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return [
    {
      '@context': 'https://schema.org',
      '@graph': [organization, website, software, webPage, faqPage],
    },
  ];
}
